import React, { useState, useEffect } from "react";
import Image from "next/image";
import Icon from "@components/Icon";
import { colours, fonts, useDeviceDetect } from "@helpers";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import type { IModuleFooterFields } from "@types-contentful";
import Link from "next/link";
import { usePathname } from "next/navigation";
import styled from "styled-components";
import { isMobile, isTablet } from "react-device-detect";

const Container = styled.footer`
  height: max-content;
  background-color: ${colours.PEACH};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 40px 35px;
  margin-top: -10px !important;
  margin-bottom: 0px !important;
  @media (min-width: 834px) {
    align-items: unset;
    flex-direction: row;
    padding: 40px 10%;
  }
  @media (min-width: 834px) {
    > div {
      width: 50%;
    }
  }
`;

const ImageContainer = styled.div`
img {
	position: relative !important;
}
margin: 0 !important;
`;
const FooterImage = styled(Image)<{ isMobileImageAddedToContenful: boolean }>`
  display: ${(isMobileImageAddedToContenful) =>
		isMobileImageAddedToContenful && "none"};
  @media (min-width: 834px) {
    display: flex;
  }
`;
const MobileImage = styled(Image)`
  width: 100% !important;
  height: auto !important;
  @media (min-width: 834px) {
    display: none;
  }
`;

const HeadlineContainer = styled.div`
  > h2 {
    max-width: 300px;
    @media (min-width: 834px) {
      max-width: 330px;
      margin-bottom: 0px;
    }
  }
`;

const Logo = styled(Image)`
  margin-top: 24px;
  margin-bottom: 24px;
  @media (min-width: 834px) {
    margin-top: 32px;
  }
`;

const LinksSection = styled.span`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(3, 40px);
  margin-bottom: 48px;
  @media (min-width: 834px) {
    grid-template-rows: repeat(3, 48px);
  }
  * > a {
    text-decoration: none;
    font-size: ${fonts.CAPTION};
    font-weight: bold;
	color: ${colours.MIDNIGHT_BLUE};
	height: 24px;
	display: block;
  }

`;

const SocialLinksSection = styled.span`
  display: flex;
  flex-direction: row;
  gap: 25px;
  margin-top: 13px;
  @media (min-width: 834px) {
    margin-top: 7px;
    gap: 30px;
  }
`;
const AppTitle = styled.p`
  margin-top: 32px;
  font-size: ${fonts.H6};
`;

const FooterAppSection = styled.section`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: 24px;
`;

const SocialLink = styled(Link)`
  text-decoration: none;

  & img {
    :hover {
      cursor: pointer;
    }
  }
`;

const StyleInnerContentText = styled.p`
  margin-top: 32px;
  font-size: 12px;

  > a {
    font-size: ${fonts.CAPTION};
  }
`;

const QRCodeImage = styled.div`
  width: 150px;
  height: 150px;
  margin-top: 24px; 
  img {
	width: 100%;
  }
`;

const iconSizes = {
	iconMobile: 22,
	iconTablet: 22,
	iconLaptop: 22,
	iconDesktop: 22,
	iconDesktopLarge: 22,
};

const FooterModule = ({
	name,
	headline,
	legalLinks,
	copyRightsText,
	socialLinks,
	image,
	mobileImage,
}: IModuleFooterFields) => {
	const isMobileImageAddedToContenful: boolean = mobileImage !== undefined;
	const { isAndroid, isIOS, isMobile, isTablet } = useDeviceDetect();

	const [isIOSPhone, setIsIOSPhone] = useState(false);
	useEffect(() => {
		setIsIOSPhone(isIOS);
	}, [isAndroid, isIOS]);
	const pathname = usePathname();
	const [deviceType, setDeviceType] = useState("mobile");

	useEffect(() => {
		if (isMobile || isTablet) {
			setDeviceType("mobile");
		} else {
			setDeviceType("desktop");
		}
	}, []);
	const showQRCode = deviceType === "desktop";

	const loaderProp = ({ src }) => {
		return src;
	};

	const mobileImageUrl = mobileImage?.fields?.file?.url?.startsWith("http")
		? mobileImage.fields.file.url
		: `https:${mobileImage?.fields?.file?.url}`;

	return (
		<>
			<ImageContainer>
				{image ? (
					<FooterImage
						src={
							image?.fields?.file?.url?.startsWith("http")
								? image.fields.file.url
								: `https:${image?.fields?.file?.url}`
						}
						alt={
							image?.fields?.description !== ""
								? image?.fields?.description
								: image?.fields?.title
						}
						fill
						isMobileImageAddedToContenful={isMobileImageAddedToContenful}
						loader={loaderProp}
					/>
				) : (
					// this is a spacing div to ensure the footer is the same height as the image
					// using a null won't work or a empty div
					<div style={{ visibility: "hidden" }} aria-hidden="true">
						spacing
					</div>
				)}
			</ImageContainer>

			{mobileImage && (
				<MobileImage
					id="whatthe"
					src={mobileImageUrl}
					alt={
						mobileImage?.fields?.description !== ""
							? mobileImage?.fields?.description
							: mobileImage?.fields?.title
					}
					width={833}
					height={108}
					style={{ margin: "auto" }}
				/>
			)}

			<Container>
				<HeadlineContainer>
					{headline && <h2>{headline}</h2>}
					<a href="/">
						<Logo
							src="/images/felixmobile_Logo_RGB_Primary_Navy_withWhite.png"
							alt="Felix Mobile footer logo"
							width={134}
							height={54}
						/>
					</a>
				</HeadlineContainer>
				<div>
					<LinksSection>
						{legalLinks?.links?.map(
							(item: { link: string; linkText: string }) => (
								<span key={item.link}>
									{!item.link.includes("help") ? (
										<Link
											href={item.link}
											data-gtm-cta-position={name}
											data-gtm-cta-text={item.linkText}
											onClick={() => {
												mixpanelTrack("footer_hypelink_clicked", {
													cta_location: pathname,
													cta_name: item.linkText,
												});
											}}
										>
											{item.linkText}
										</Link>
									) : (
										<Link
											href={item.link}
											onClick={() => {
												mixpanelTrack("need_help_clicked", {
													cta_url: name,
												});
											}}
										>
											{item.linkText}
										</Link>
									)}
								</span>
							),
						)}
					</LinksSection>
					<SocialLinksSection>
						{socialLinks?.links?.map(
							(item: { link: string; linkText: string; iconRef: string }) => (
								<SocialLink
									key={item.link}
									href={item.link}
									target="_blank"
									rel="noopener noreferrer"
								>
									<Icon
										className="clickable"
										iconName={item.iconRef}
										iconSizes={iconSizes}
										alt={item?.linkText}
										onClick={() =>
											mixpanelTrack("socialmedia_hypelink_clicked", {
												cta_location: pathname,
												cta_name: item.iconRef,
											})
										}
									/>
								</SocialLink>
							),
						)}
					</SocialLinksSection>
					<AppTitle>Download the app</AppTitle>
					{showQRCode ? (
						<QRCodeImage>
							<Image
								src="/images/footer_qrcode.jpg"
								alt="qr code to take you to the felix app"
								width={150}
								height={150}
							/>
						</QRCodeImage>
					) : (
						<FooterAppSection>
							{isIOSPhone ? (
								<Link
									href="https://flx.mobi/bQkA1fQX7db"
									target="_blank"
									rel="noreferrer"
								>
									<Image
										className="clickable"
										alt="Get it on Appstore"
										src="/images/AppStoreBadge.png"
										width={145}
										height={45}
										data-gtm-cta-text="appleUrl"
										data-gtm-cta-position="footer"
										onClick={() => {
											mixpanelTrack("download_app_clicked", {
												cta_position: "footer",
												cta_text: "appleUrl",
											});
										}}
									/>
								</Link>
							) : (
								<Link
									href="https://flx.mobi/FagEfRLX7db"
									target="_blank"
									rel="noreferrer"
								>
									<Image
										className="clickable"
										alt="Get it on Google Play"
										src="/images/PlayStoreBadge.png"
										width={145}
										height={45}
										data-gtm-cta-text="androidUrl"
										data-gtm-cta-position="footer"
										onClick={() => {
											mixpanelTrack("download_app_clicked", {
												cta_position: "footer",
												cta_text: "androidUrl",
											});
										}}
									/>
								</Link>
							)}
						</FooterAppSection>
					)}

					<StyleInnerContentText>
						<a style={{ textDecoration: "none", cursor: "default" }} href="/">
							{copyRightsText}
						</a>
					</StyleInnerContentText>
				</div>
			</Container>
		</>
	);
};

export default FooterModule;
